import { colors } from '@scouts/ui'

import config from '../config'
import {
    QUARTER_DATE_RANGE,
    QUARTER_MONTHS,
    QUARTER_Q1,
    QUARTER_Q2,
    QUARTER_Q3,
    QUARTER_Q4,
    QUARTER_TITLES,
    QUARTERLY_TAX_RETURN_STATUS_PRIORITY,
    QUARTERS,
    TAX_RETURN_STATUS_ANSWERING_QUESTIONS,
    TAX_RETURN_STATUS_AWAITING_CUSTOMER_APPROVAL,
    TAX_RETURN_STATUS_CANCELLED,
    TAX_RETURN_STATUS_COMPLETED_NOT_FILED,
    TAX_RETURN_STATUS_CREATED,
    TAX_RETURN_STATUS_FILED,
    TAX_RETURN_STATUS_FILING_NOT_REQUIRED,
    TAX_RETURN_STATUS_INFORMATION_REQUIRED,
    TAX_RETURN_STATUS_READY_TO_FILE,
    TAX_RETURN_STATUS_STARTED,
    TAX_RETURN_STATUS_SUBMITTED,
    TAX_RETURN_STATUS_UPLOADING_DOCUMENTS,
} from '../constants'
import { isQuarterlyTaxReturnsStatusAtLeast } from '../helpers'
import { addDays, getCurrentYear, getDate, getIsDateExceeded, getMonth, getYear } from '../helpers/dates'

export const computeQuarterlyTaxReturnTotalTaxDue = ({
    incomeTaxDue,
    vatDue,
    euVatDue,
    withholdingTaxDueInterest,
    withholdingTaxDuePersonalIncome,
    withholdingTaxDueRent,
}) =>
    (Number(incomeTaxDue) || 0) +
    computeVatToPay(vatDue) +
    (Number(euVatDue) || 0) +
    (Number(withholdingTaxDueInterest) || 0) +
    (Number(withholdingTaxDuePersonalIncome) || 0) +
    (Number(withholdingTaxDueRent) || 0)

export const computeVatToPay = (vatDue) => Math.max(0, vatDue) || 0

export const computeVatCredit = (vatDue) => Math.abs(Math.min(0, vatDue)) || 0

const getQuartersYear = (year, quarter) => (quarter === QUARTER_Q4 ? Number(year) + 1 : year)

export const PREVIOUS_QUARTER = {
    [QUARTER_Q1]: QUARTER_Q4,
    [QUARTER_Q2]: QUARTER_Q1,
    [QUARTER_Q3]: QUARTER_Q2,
    [QUARTER_Q4]: QUARTER_Q3,
}

const QUARTER_FILING_MONTH_START_DATE = {
    [QUARTER_Q1]: `04-01`,
    [QUARTER_Q2]: `07-01`,
    [QUARTER_Q3]: `10-01`,
    [QUARTER_Q4]: `01-01`,
}
const getQuarterFilingMonthStartDate = (year, quarter) =>
    `${getQuartersYear(year, quarter)}-${QUARTER_FILING_MONTH_START_DATE[quarter]}`

const QUARTER_FILING_MONTH_END_DATE = {
    [QUARTER_Q1]: `04-30`,
    [QUARTER_Q2]: `07-31`,
    [QUARTER_Q3]: `10-31`,
    [QUARTER_Q4]: `01-31`,
}
const getQuarterFilingMonthEndDate = (year, quarter) =>
    `${getQuartersYear(year, quarter)}-${QUARTER_FILING_MONTH_END_DATE[quarter]}`

const QUARTER_DIRECT_DEBIT_DEADLINE = {
    [QUARTER_Q1]: `04-15`,
    [QUARTER_Q2]: `07-15`,
    [QUARTER_Q3]: `10-15`,
    [QUARTER_Q4]: `01-20`,
}
export const getQuarterDirectDebitDeadline = (year, quarter) =>
    `${getQuartersYear(year, quarter)}-${QUARTER_DIRECT_DEBIT_DEADLINE[quarter]}`

export const QUARTER_FILING_DEADLINE = {
    [QUARTER_Q1]: `04-20`,
    [QUARTER_Q2]: `07-20`,
    [QUARTER_Q3]: `10-20`,
    [QUARTER_Q4]: `01-31`,
}
export const getQuarterFilingDeadline = (year, quarter) =>
    `${getQuartersYear(year, quarter)}-${QUARTER_FILING_DEADLINE[quarter]}`

const QUARTER_FILING_DEADLINE_TITLES = {
    [QUARTER_Q1]: `20 de abril`,
    [QUARTER_Q2]: `20 de julio`,
    [QUARTER_Q3]: `20 de octubre`,
    [QUARTER_Q4]: `31 de enero`,
}
export const getQuarterlyFilingDeadlineTitle = (quarter) => QUARTER_FILING_DEADLINE_TITLES[quarter]

const QUARTER_FILING_NEAR_DEADLINE = {
    [QUARTER_Q1]: `04-11`,
    [QUARTER_Q2]: `07-11`,
    [QUARTER_Q3]: `10-11`,
    [QUARTER_Q4]: `01-22`,
}
const getQuarterFilingNearDeadline = (year, quarter) =>
    `${getQuartersYear(year, quarter)}-${QUARTER_FILING_NEAR_DEADLINE[quarter]}`

const QUARTER_END_DATE = {
    [QUARTER_Q1]: `03-31`,
    [QUARTER_Q2]: `06-30`,
    [QUARTER_Q3]: `09-30`,
    [QUARTER_Q4]: `12-31`,
}
const getQuarterEndDate = (year, quarter) => `${year}-${QUARTER_END_DATE[quarter]}`

const QUARTER_START_DATE = {
    [QUARTER_Q1]: `01-01`,
    [QUARTER_Q2]: `04-01`,
    [QUARTER_Q3]: `07-01`,
    [QUARTER_Q4]: `10-01`,
}
const getQuarterStartDate = (year, quarter) => `${year}-${QUARTER_START_DATE[quarter]}`

export const getQuarterDateInterval = (year, quarter) => ({
    end: new Date(getQuarterEndDate(year, quarter)),
    start: new Date(getQuarterStartDate(year, quarter)),
})

export const getIsBookkeepingQuarter = (year, quarter) =>
    getIsDateExceeded(
        getQuarterStartDate(year, quarter),
        getQuarterStartDate(config.bookkeepingReleaseYear, config.bookkeepingReleaseQuarter),
        {
            inclusive: true,
        }
    )

const getQuarterStartMonth = (quarter) => 3 * (quarter - 1) + 1
export const getQuarterMonths = (quarter) => ({
    startMonth: getQuarterStartMonth(quarter),
    middleMonth: getQuarterStartMonth(quarter) + 1,
    endMonth: getQuarterStartMonth(quarter) + 2,
    filingMonth: (getQuarterStartMonth(quarter) + 3) % 12,
})

export const getPreviousQuarterNumber = () => {
    const date = new Date()
    const month = getMonth(date)
    const q = Math.ceil(month / 3) - 1
    return q === 0 ? 4 : q
}

const previousQuarter = () => `Q${getPreviousQuarterNumber()}`

const previousQuarterWithTaxYear = () => {
    const quarter = previousQuarter()
    return [quarter === QUARTER_Q4 ? getCurrentYear() - 1 : getCurrentYear(), quarter]
}

export const getPreviousQuarter = ({ taxYear, quarter }) => ({
    taxYear: quarter === QUARTER_Q1 ? taxYear - 1 : taxYear,
    quarter: PREVIOUS_QUARTER[quarter],
})

export const getIsPreviousQuarter = (taxYear, quarter) => {
    const [prevTaxYear, prevQuarter] = previousQuarterWithTaxYear()
    return Number(taxYear) === Number(prevTaxYear) && quarter === prevQuarter
}

export const getQuarterNumber = (date = getDate()) => {
    const month = getMonth(date)
    return Math.ceil(month / 3)
}

export const quarterOfTheYear = (date = getDate()) => `Q${getQuarterNumber(date)}`

export const getIsFutureQuarter = (taxYear, quarter) => {
    const currentQuarter = quarterOfTheYear()

    return Number(taxYear) > getCurrentYear() || (Number(taxYear) === getCurrentYear() && quarter > currentQuarter)
}

export const getIsQuarterlyTaxReturnStatusFinal = (status) =>
    [
        TAX_RETURN_STATUS_CANCELLED,
        TAX_RETURN_STATUS_COMPLETED_NOT_FILED,
        TAX_RETURN_STATUS_FILED,
        TAX_RETURN_STATUS_FILING_NOT_REQUIRED,
    ].includes(status)

export const getIsQuarterFilingMonthStarted = ({ quarter, year = getCurrentYear(), date = getDate() }) =>
    getIsDateExceeded(date, getQuarterFilingMonthStartDate(year, quarter), { inclusive: true })

export const getIsQuartersEndDateExceeded = ({ quarter, year = getCurrentYear(), date = getDate() }) =>
    getIsDateExceeded(date, getQuarterEndDate(year, quarter))

export const getIsQuartersFilingDeadlineExceeded = ({ quarter, year = getCurrentYear(), date = getDate() }) =>
    getIsDateExceeded(date, getQuarterFilingDeadline(year, quarter))

export const getIsQuartersFilingNearDeadlineExceeded = ({ quarter, year = getCurrentYear(), date = getDate() }) =>
    getIsDateExceeded(date, getQuarterFilingNearDeadline(year, quarter))

export const getIsQuartersDirectDebitDeadlineExceeded = ({ quarter, year = getCurrentYear(), date = getDate() }) =>
    getIsDateExceeded(date, getQuarterDirectDebitDeadline(year, quarter))

export const getIsQuartersFilingMonthEndDateExceeded = ({ quarter, year = getCurrentYear(), date = getDate() }) =>
    getIsDateExceeded(date, getQuarterFilingMonthEndDate(year, quarter))

export const getIsCurrentQuarter = (taxYear, quarter) => {
    const currentQuarter = quarterOfTheYear()
    return Number(taxYear) === getCurrentYear() && currentQuarter === quarter
}

export const formatQuarterDateRange = (taxYear, quarter) => `${QUARTER_DATE_RANGE[quarter]} ${taxYear}`

export const formatQuarterWithMonths = (quarter = quarterOfTheYear()) =>
    `${QUARTER_TITLES[quarter]} · ${QUARTER_MONTHS[quarter]}`

export const quarterEndDateWithoutYear = ({ quarter = quarterOfTheYear() } = {}) => {
    switch (quarter) {
        case QUARTER_Q1:
            return '31 de marzo'
        case QUARTER_Q2:
            return '30 de junio'
        case QUARTER_Q3:
            return '30 de septiembre'
        case QUARTER_Q4:
            return '31 de diciembre'
        default:
            return null
    }
}

export const getQuarterlyTaxReturnDeadlines = ({
    quarter = quarterOfTheYear(),
    year = getCurrentYear(),
    date = getDate(),
} = {}) => ({
    quarterDirectDebitDeadline: getQuarterDirectDebitDeadline(year, quarter),
    quarterFilingDeadline: getQuarterFilingDeadline(year, quarter),
    isQuarterlyTaxReturnDirectDebitDeadlineClose: getIsQuartersDirectDebitDeadlineExceeded({
        year,
        quarter,
        date: addDays(6)(date),
    }),
    isQuarterlyTaxReturnDirectDebitDeadlineExceeded: getIsQuartersDirectDebitDeadlineExceeded({
        year,
        quarter,
        date: getDate(date),
    }),
    isQuarterlyTaxReturnFilingDeadlineClose: getIsQuartersFilingDeadlineExceeded({
        year,
        quarter,
        date: addDays(6)(date),
    }),
    isQuarterlyTaxReturnFilingDeadlineExceeded: getIsQuartersFilingDeadlineExceeded({
        year,
        quarter,
        date: getDate(date),
    }),
    isQuarterlyTaxReturnFilingMonthEndDateExceeded: getIsQuartersFilingMonthEndDateExceeded({
        year,
        quarter,
        date: getDate(date),
    }),
})

export const getCurrentNotOverDueYear = (date = getDate()) => {
    const datesYear = getYear(date)
    const isJanuary = getMonth(date) === 1

    return isJanuary ? datesYear - 1 : datesYear
}

const getIsWithinQuartersFilingMonthEndDates = ({ quarter, date = getDate() }) =>
    getIsQuartersFilingMonthEndDateExceeded({
        quarter: PREVIOUS_QUARTER[quarter],
        year: getCurrentNotOverDueYear(date),
        date,
    }) &&
    !getIsQuartersFilingMonthEndDateExceeded({
        quarter,
        year: getCurrentNotOverDueYear(date),
        date,
    })

export const getCurrentNotOverDueQuarter = (date = getDate()) =>
    QUARTERS.reduce((notOverDueQuarter, quarter) =>
        getIsWithinQuartersFilingMonthEndDates({ quarter, date }) ? quarter : notOverDueQuarter
    )

const QUARTERLY_TAX_RETURN_STATUSES_COLORS = [
    {
        color: colors.surface.attention,
        statuses: [
            TAX_RETURN_STATUS_CREATED,
            TAX_RETURN_STATUS_STARTED,
            TAX_RETURN_STATUS_UPLOADING_DOCUMENTS,
            TAX_RETURN_STATUS_ANSWERING_QUESTIONS,
            TAX_RETURN_STATUS_INFORMATION_REQUIRED,
            TAX_RETURN_STATUS_AWAITING_CUSTOMER_APPROVAL,
        ],
    },
    {
        color: colors.surface.affirm,
        statuses: [TAX_RETURN_STATUS_FILED],
    },
    {
        color: colors.surface.note,
        statuses: [TAX_RETURN_STATUS_CANCELLED],
    },
]

export const getQuarterlyTaxReturnStatusColor = (status) => {
    const statusColor = QUARTERLY_TAX_RETURN_STATUSES_COLORS.find((statusColors) =>
        statusColors.statuses.includes(status)
    )
    return statusColor?.color || colors.surface.highlight
}

export const getQuarterlyTaxReturnDashboardColor = ({ isPastQuarter, status }) => {
    if (!isPastQuarter) return colors.surface.neutral
    if (!status) return colors.surface.attention
    const statusColor = QUARTERLY_TAX_RETURN_STATUSES_COLORS.find((statusColors) =>
        statusColors.statuses.includes(status)
    )
    return statusColor?.color || colors.surface.highlight
}

const sortQuarterlyTaxReturnsByPriority = (
    { status: statusA, isPastQuarter: isPastQuarterA },
    { status: statusB, isPastQuarter: isPastQuarterB }
) => {
    if (isPastQuarterA && isPastQuarterB)
        return QUARTERLY_TAX_RETURN_STATUS_PRIORITY[statusA] - QUARTERLY_TAX_RETURN_STATUS_PRIORITY[statusB]
    if (isPastQuarterA) return -1
    if (isPastQuarterB) return 1
    return 0
}

export const getQuarterlyTaxReturnsOrderedByPriority = (quarterlyTaxReturns) =>
    [...quarterlyTaxReturns].sort(sortQuarterlyTaxReturnsByPriority)

export const getIsQuarterlyTaxReturnUrgent = ({ status, isQuarterFillingDatePast, isPastQuarter }) => {
    if (!status) return isPastQuarter && !isQuarterFillingDatePast
    return QUARTERLY_TAX_RETURN_STATUS_PRIORITY[status] <= 10 && isPastQuarter
}

export const getQuarterlyTaxReturnDetail = ({ quarterlyTaxReturns, taxYear, quarter }) => {
    const quarterlyTaxReturn = quarterlyTaxReturns.find(
        ({ taxYear: itemTaxYear, quarter: itemQuarter }) => itemTaxYear === String(taxYear) && itemQuarter === quarter
    )

    const isLockedForUserEditing = quarterlyTaxReturn?.lockedForUserEditing
    const status = quarterlyTaxReturn?.status

    const isFilingRequired = status !== TAX_RETURN_STATUS_FILING_NOT_REQUIRED

    const isAtLeastReadyForApproval = isQuarterlyTaxReturnsStatusAtLeast(
        status,
        TAX_RETURN_STATUS_AWAITING_CUSTOMER_APPROVAL
    )

    const isAtLeastReadyToFile = isQuarterlyTaxReturnsStatusAtLeast(status, TAX_RETURN_STATUS_READY_TO_FILE)

    const isAtLeastSubmitted = isQuarterlyTaxReturnsStatusAtLeast(status, TAX_RETURN_STATUS_SUBMITTED)

    const { incomeTaxDue, vatDue, withheldTaxDue } = quarterlyTaxReturn || {}

    const totalTaxDue = computeQuarterlyTaxReturnTotalTaxDue({ incomeTaxDue, vatDue, withheldTaxDue })

    const isPastQuarter = getIsQuartersEndDateExceeded({ quarter, year: taxYear })
    const isPreviousQuarter = getIsPreviousQuarter(taxYear, quarter)
    const isQuarterFillingDatePast = getIsQuartersFilingDeadlineExceeded({ quarter, year: taxYear })
    const isQuarterFillingNearDeadlinePast = getIsQuartersFilingNearDeadlineExceeded({ quarter, year: taxYear })

    const isCurrentQuarter = getIsCurrentQuarter(taxYear, quarter)
    const isCurrentOrFutureQuarter = isCurrentQuarter || getIsFutureQuarter(taxYear, quarter)

    const isBookkeepingQuarter = getIsBookkeepingQuarter(taxYear, quarter)

    return {
        isAtLeastReadyForApproval,
        isAtLeastReadyToFile,
        isAtLeastSubmitted,
        isCancelled: status === TAX_RETURN_STATUS_CANCELLED,
        isCurrentOrFutureQuarter,
        isCurrentQuarter,
        isDeleteAllowed: isFilingRequired && !isAtLeastSubmitted,
        isLockedForUserEditing,
        isPastQuarter,
        isPreviousQuarter,
        isQuarterFillingDatePast,
        isQuarterFillingNearDeadlinePast,
        quarterlyTaxReturn,
        status,
        totalTaxDue,
        isBookkeepingQuarter,
    }
}
